import type { DocumentType } from '@makila/fiscal';
import { creditedFrom, mergeOrder, type CashMovement, type CashSession, type CatalogItem, type IssuedDocument, type LicenceInfo, type Order, type OutboxEvent, type TerminalSeries, type TerminalStore, type TerminalTx } from '@makila/pos-core';

/**
 * Armazenamento do terminal no browser (IndexedDB) com a semântica que o
 * pos-core exige: uma venda grava série, documento e evento TODOS ou NENHUM.
 *
 * O IndexedDB fecha sozinho uma transacção quando o código espera por outra
 * coisa (aqui, a assinatura WebCrypto). Por isso `transaction(fn)` corre `fn`
 * sobre uma cópia em memória, junta as escritas e só no fim as grava numa
 * única transacção IndexedDB, com durability 'strict' (o browser só confirma
 * depois de o disco confirmar — o que importa num corte de energia). Se essa
 * gravação falhar, a cópia é deitada fora e nada mudou.
 *
 * Um só separador pode ser a caixa (Web Locks, em main.tsx): o espelho em
 * memória nunca fica desactualizado por outro separador.
 */

const DB_NAME = 'makila-pos';
const DB_VERSION = 4;
const STORES = ['kv', 'series', 'outbox', 'documents', 'catalog', 'cashSessions', 'cashMovements', 'orders'] as const;

const seriesKey = (t: DocumentType, y: number, training: boolean) => `${t}:${y}:${training}`;
const docKey = (d: IssuedDocument) => `${d.seriesId}:${d.number}`;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((ok, ko) => { r.onsuccess = () => ok(r.result); r.onerror = () => ko(r.error); });
}
function done(tx: IDBTransaction): Promise<void> {
  return new Promise((ok, ko) => {
    tx.oncomplete = () => ok();
    tx.onerror = () => ko(tx.error ?? new Error('Falha ao gravar no dispositivo'));
    tx.onabort = () => ko(tx.error ?? new Error('Gravação cancelada no dispositivo'));
  });
}

export function openPosDb(factory: IDBFactory = indexedDB, name = DB_NAME): Promise<IDBDatabase> {
  return new Promise((ok, ko) => {
    const r = factory.open(name, DB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
      if (!db.objectStoreNames.contains('series')) db.createObjectStore('series');
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'seq' });
      if (!db.objectStoreNames.contains('documents')) db.createObjectStore('documents').createIndex('byEntry', 'systemEntryDate');
      if (!db.objectStoreNames.contains('catalog')) db.createObjectStore('catalog');
      // v2 (F2): caixa. As vendas já gravadas ficam como estão.
      if (!db.objectStoreNames.contains('cashSessions')) db.createObjectStore('cashSessions');
      if (!db.objectStoreNames.contains('cashMovements')) db.createObjectStore('cashMovements').createIndex('bySession', 'sessionId');
      const docs = r.transaction!.objectStore('documents');
      if (!docs.indexNames.contains('bySession')) docs.createIndex('bySession', 'cashSessionId');
      // v3: notas de crédito por documento de origem (o que já foi devolvido).
      if (!docs.indexNames.contains('byOrigin')) docs.createIndex('byOrigin', ['origin.seriesId', 'origin.number']);
      // v4 (F5): comandas do restaurante.
      if (!db.objectStoreNames.contains('orders')) db.createObjectStore('orders', { keyPath: 'id' });
    };
    r.onsuccess = () => ok(r.result);
    r.onerror = () => ko(r.error);
    r.onblocked = () => ko(new Error('A base de dados local está aberta noutro separador'));
  });
}

interface Mirror { series: Map<string, TerminalSeries>; lastSeq: number; openSession: CashSession | null; lastZ: number }

export class IdbTerminalStore implements TerminalStore {
  private queue: Promise<unknown> = Promise.resolve();

  private constructor(private readonly db: IDBDatabase, private mirror: Mirror) {}

  static async open(db: IDBDatabase): Promise<IdbTerminalStore> {
    const tx = db.transaction(['series', 'kv', 'cashSessions'], 'readonly');
    const series = await req(tx.objectStore('series').getAll()) as TerminalSeries[];
    const lastSeq = (await req(tx.objectStore('kv').get('lastSeq')) as number | undefined) ?? 0;
    const lastZ = (await req(tx.objectStore('kv').get('lastZ')) as number | undefined) ?? 0;
    const openId = await req(tx.objectStore('kv').get('openSessionId')) as string | undefined;
    const openSession = openId ? (await req(tx.objectStore('cashSessions').get(openId)) as CashSession | undefined) ?? null : null;
    return new IdbTerminalStore(db, { series: new Map(series.map((s) => [seriesKey(s.docType, s.year, s.training), s])), lastSeq, openSession, lastZ });
  }

  /** Séries recebidas na activação (substitui as que existirem com a mesma chave). */
  async seed(series: TerminalSeries[]): Promise<void> {
    const tx = this.db.transaction('series', 'readwrite', { durability: 'strict' });
    for (const s of series) tx.objectStore('series').put(s, seriesKey(s.docType, s.year, s.training));
    await done(tx);
    for (const s of series) this.mirror.series.set(seriesKey(s.docType, s.year, s.training), s);
  }

  transaction<T>(fn: (tx: TerminalTx) => Promise<T>): Promise<T> {
    const run = this.queue.then(async () => {
      const work: Mirror = { ...this.mirror, series: new Map(this.mirror.series) };
      const writes = { series: [] as TerminalSeries[], outbox: [] as OutboxEvent[], documents: [] as IssuedDocument[], sessions: [] as CashSession[], movements: [] as CashMovement[], orders: new Map<string, Order>() };
      const out = await fn({
        getSeries: async (t, y, training) => work.series.get(seriesKey(t, y, training)) ?? null,
        putSeries: async (s) => { work.series.set(seriesKey(s.docType, s.year, s.training), s); writes.series.push(s); },
        lastSeq: async () => work.lastSeq,
        appendOutbox: async (e) => {
          if (e.seq !== work.lastSeq + 1) throw new Error(`seq ${e.seq} fora de ordem (último ${work.lastSeq})`);
          work.lastSeq = e.seq;
          writes.outbox.push(e);
        },
        saveDocument: async (d) => { writes.documents.push(d); },
        openSession: async () => work.openSession,
        putSession: async (x) => {
          writes.sessions.push(x);
          if (x.status === 'open') work.openSession = x;
          else { if (work.openSession?.id === x.id) work.openSession = null; work.lastZ = Math.max(work.lastZ, x.zNumber ?? 0); }
        },
        addMovement: async (m) => { writes.movements.push(m); },
        lastZ: async () => work.lastZ,
        sessionDocuments: async (id) => [...await this.byIndex<IssuedDocument>('documents', 'bySession', id), ...writes.documents.filter((d) => d.cashSessionId === id)],
        sessionMovements: async (id) => [...await this.byIndex<CashMovement>('cashMovements', 'bySession', id), ...writes.movements.filter((m) => m.sessionId === id)],
        creditedQuantities: async (seriesId, number) => creditedFrom([...await this.byIndex<IssuedDocument>('documents', 'byOrigin', [seriesId, number]), ...writes.documents], seriesId, number),
        getOrder: async (id) => writes.orders.get(id) ?? (await req(this.db.transaction('orders', 'readonly').objectStore('orders').get(id)) as Order | undefined) ?? null,
        putOrder: async (o) => { writes.orders.set(o.id, o); },
      });
      const tx = this.db.transaction(['series', 'outbox', 'documents', 'kv', 'cashSessions', 'cashMovements', 'orders'], 'readwrite', { durability: 'strict' });
      for (const s of writes.series) tx.objectStore('series').put(s, seriesKey(s.docType, s.year, s.training));
      // add (não put): um evento com o mesmo seq nunca substitui outro.
      for (const e of writes.outbox) tx.objectStore('outbox').add(e);
      for (const d of writes.documents) tx.objectStore('documents').add(d, docKey(d));
      for (const x of writes.sessions) tx.objectStore('cashSessions').put(x, x.id);
      for (const m of writes.movements) tx.objectStore('cashMovements').add(m, m.id);
      for (const o of writes.orders.values()) tx.objectStore('orders').put(o);
      tx.objectStore('kv').put(work.lastSeq, 'lastSeq');
      tx.objectStore('kv').put(work.lastZ, 'lastZ');
      tx.objectStore('kv').put(work.openSession?.id ?? null, 'openSessionId');
      await done(tx);
      this.mirror = work;
      return out;
    });
    this.queue = run.catch(() => {});
    return run;
  }

  private async byIndex<T>(store: 'documents' | 'cashMovements', index: string, key: IDBValidKey): Promise<T[]> {
    return await req(this.db.transaction(store, 'readonly').objectStore(store).index(index).getAll(key)) as T[];
  }

  get openSession(): CashSession | null { return this.mirror.openSession; }

  /** Sessões fechadas, mais recentes primeiro (consulta de relatórios Z no terminal). */
  async closedSessions(limit = 30): Promise<CashSession[]> {
    const all = await req(this.db.transaction('cashSessions', 'readonly').objectStore('cashSessions').getAll()) as CashSession[];
    return all.filter((x) => x.status === 'closed').sort((a, b) => (b.zNumber ?? 0) - (a.zNumber ?? 0)).slice(0, limit);
  }

  async sessionData(id: string): Promise<{ docs: IssuedDocument[]; movements: CashMovement[] }> {
    return { docs: await this.byIndex('documents', 'bySession', id), movements: await this.byIndex('cashMovements', 'bySession', id) };
  }

  async pendingOutbox(limit: number): Promise<OutboxEvent[]> {
    const acked = await this.kv<number>('ackedSeq') ?? 0;
    const tx = this.db.transaction('outbox', 'readonly');
    return await req(tx.objectStore('outbox').getAll(IDBKeyRange.lowerBound(acked, true), limit)) as OutboxEvent[];
  }

  async setAcked(seq: number): Promise<void> { await this.setKv('ackedSeq', seq); }
  async catalogCursor(): Promise<number> { return await this.kv<number>('catalogCursor') ?? 0; }

  async applyCatalog(items: CatalogItem[], nextCursor: number): Promise<void> {
    const tx = this.db.transaction(['catalog', 'kv'], 'readwrite', { durability: 'strict' });
    for (const i of items) tx.objectStore('catalog').put(i, `${i.type}:${i.id}`);
    // O cursor avança na mesma transacção dos itens: nunca há cursor à frente do catálogo.
    tx.objectStore('kv').put(nextCursor, 'catalogCursor');
    await done(tx);
  }

  async saveLicence(l: LicenceInfo): Promise<void> { await this.setKv('licence', l); }

  async ordersCursor(): Promise<number> { return await this.kv<number>('ordersCursor') ?? 0; }

  /** Comandas da nuvem: juntam-se às locais (pela fila das transacções, para não cruzar com uma gravação da caixa). */
  applyOrders(orders: Order[], nextCursor: number): Promise<void> {
    const run = this.queue.then(async () => {
      const tx = this.db.transaction(['orders', 'kv'], 'readwrite', { durability: 'strict' });
      const os = tx.objectStore('orders');
      for (const o of orders) {
        const cur = await req(os.get(o.id)) as Order | undefined;
        os.put(cur ? mergeOrder(cur, o) : o);
      }
      tx.objectStore('kv').put(nextCursor, 'ordersCursor');
      await done(tx);
    });
    this.queue = run.catch(() => {});
    return run;
  }

  /** Comandas abertas e as fechadas das últimas horas (o mapa de mesas e "recentes"). */
  async orders(): Promise<Order[]> {
    const all = await req(this.db.transaction('orders', 'readonly').objectStore('orders').getAll()) as Order[];
    const since = new Date(Date.now() - 12 * 3_600_000).toISOString();
    return all.filter((o) => o.status === 'open' || o.updatedAt > since);
  }

  /** Todas as comandas (as ordens de serviço ficam abertas dias e os números contam-se em todas). */
  async allOrders(): Promise<Order[]> {
    return await req(this.db.transaction('orders', 'readonly').objectStore('orders').getAll()) as Order[];
  }

  // ── Leituras para os ecrãs ────────────────────────────────────────────────

  async kv<T>(key: string): Promise<T | undefined> {
    return await req(this.db.transaction('kv', 'readonly').objectStore('kv').get(key)) as T | undefined;
  }

  async setKv(key: string, value: unknown): Promise<void> {
    const tx = this.db.transaction('kv', 'readwrite', { durability: 'strict' });
    tx.objectStore('kv').put(value, key);
    await done(tx);
  }

  async catalog(type: CatalogItem['type']): Promise<CatalogItem[]> {
    const all = await req(this.db.transaction('catalog', 'readonly').objectStore('catalog').getAll()) as CatalogItem[];
    return all.filter((i) => i.type === type);
  }

  /** Documento deste terminal pelo número completo (para devoluções). */
  async findDocument(fullNumber: string): Promise<IssuedDocument | null> {
    const all = await req(this.db.transaction('documents', 'readonly').objectStore('documents').getAll()) as IssuedDocument[];
    const want = fullNumber.trim().toUpperCase().replace(/\s+/g, ' ');
    return all.find((d) => d.fullNumber.toUpperCase() === want) ?? null;
  }

  /** Últimos documentos emitidos neste terminal (mais recentes primeiro). */
  async recentDocuments(limit = 50): Promise<IssuedDocument[]> {
    const idx = this.db.transaction('documents', 'readonly').objectStore('documents').index('byEntry');
    const out: IssuedDocument[] = [];
    await new Promise<void>((ok, ko) => {
      const c = idx.openCursor(null, 'prev');
      c.onsuccess = () => {
        const cur = c.result;
        if (!cur || out.length >= limit) return ok();
        out.push(cur.value as IssuedDocument);
        cur.continue();
      };
      c.onerror = () => ko(c.error);
    });
    return out;
  }

  async pendingCount(): Promise<number> {
    const acked = await this.kv<number>('ackedSeq') ?? 0;
    return await req(this.db.transaction('outbox', 'readonly').objectStore('outbox').count(IDBKeyRange.lowerBound(acked, true)));
  }

  /** Apaga TUDO do terminal (desactivar). Recusa se houver eventos por enviar. */
  async wipe(force = false): Promise<void> {
    if (!force && await this.pendingCount() > 0) throw new Error('Há vendas por enviar para a nuvem. Sincronize antes de desactivar o terminal.');
    const tx = this.db.transaction([...STORES], 'readwrite', { durability: 'strict' });
    for (const s of STORES) tx.objectStore(s).clear();
    await done(tx);
    this.mirror = { series: new Map(), lastSeq: 0, openSession: null, lastZ: 0 };
  }

  series(t: DocumentType, y: number, training: boolean): TerminalSeries | undefined {
    return this.mirror.series.get(seriesKey(t, y, training));
  }
}
